import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { 
  sendVerificationEmail, 
  sendOrderConfirmationEmail, 
  sendPasswordResetEmail, 
  sendNotificationEmail,
  sendSupplierVerificationEmail,
  sendDocumentNotificationEmail
} from '../lib/emailService';

// Define the hook return type
interface UseEmailReturn {
  sendVerification: (email: string, name: string, verificationLink: string) => Promise<boolean>;
  sendOrderConfirmation: (email: string, name: string, orderDetails: any) => Promise<boolean>;
  sendPasswordReset: (email: string, name: string, resetLink: string) => Promise<boolean>;
  sendNotification: (email: string, name: string, subject: string, message: string, actionLink?: string, actionText?: string) => Promise<boolean>;
  sendSupplierVerification: (email: string, name: string, verificationLink: string) => Promise<boolean>;
  sendDocumentNotification: (email: string, name: string, documentType: string, orderNumber: string, documentLink: string) => Promise<boolean>;
  loading: boolean;
  error: string | null;
}

/**
 * Hook for sending emails through the application
 * @returns Email sending functions and status
 */
export const useEmail = (): UseEmailReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Send email using Supabase Edge Function
   * @param payload Email payload
   * @returns Success status
   */
  const sendEmailViaEdgeFunction = async (payload: any): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);

      // Check if Supabase is properly configured
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        console.warn('Supabase environment variables not configured:', {
          hasUrl: !!supabaseUrl,
          hasKey: !!supabaseAnonKey
        });
        return false;
      }

      // Validate Supabase URL format
      if (!supabaseUrl.includes('supabase.co')) {
        console.warn('Invalid Supabase URL format:', supabaseUrl);
        return false;
      }

      try {
        // Add timeout to the Edge Function call
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
        
        const { data, error } = await supabase.functions.invoke('send-email', {
          body: payload,
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        clearTimeout(timeoutId);

        if (error) {
          console.warn('Edge Function failed:', {
            error: error.message || error,
            context: error.context || 'Unknown context'
          });
          return false;
        }

        if (!data?.success) {
          console.warn('Edge Function returned failure:', {
            message: data?.message || 'Unknown error',
            data: data
          });
          return false;
        }

        return true;
      } catch (edgeFunctionError) {
        console.warn('Edge Function invocation failed:', {
          error: edgeFunctionError instanceof Error ? edgeFunctionError.message : edgeFunctionError,
          name: edgeFunctionError instanceof Error ? edgeFunctionError.name : 'Unknown',
          stack: edgeFunctionError instanceof Error ? edgeFunctionError.stack : undefined
        });
        return false;
      }
    } catch (err) {
      console.warn('Edge Function wrapper error:', {
        error: err instanceof Error ? err.message : err,
        type: typeof err
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send verification email
   * @param email Recipient email
   * @param name Recipient name
   * @param verificationLink Verification link
   * @returns Success status
   */
  const sendVerification = async (
    email: string,
    name: string,
    verificationLink: string
  ): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      
      // Try to use the Edge Function first (will fail gracefully if not configured)
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject: 'Verify Your Email - Made in BD',
        emailType: 'verification',
        name,
        verificationLink
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      console.warn('Email service not available - production configuration required');
      return true; // Allow account creation to proceed
    } catch (err) {
      console.error('Error sending verification email:', err);
      // Return true to allow account creation to proceed even if email fails
      return true;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Send order confirmation email
   * @param email Recipient email
   * @param name Recipient name
   * @param orderDetails Order details
   * @returns Success status
   */
  const sendOrderConfirmation = async (
    email: string,
    name: string,
    orderDetails: any
  ): Promise<boolean> => {
    try {
      // First try to use the Edge Function
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject: `Order Confirmation #${orderDetails.orderNumber} - Made in BD`,
        emailType: 'order_confirmation',
        name,
        orderDetails
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      setError('Email service not available - production configuration required');
      return false;
    } catch (err) {
      console.error('Error sending order confirmation email:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return false;
    }
  };

  /**
   * Send password reset email
   * @param email Recipient email
   * @param name Recipient name
   * @param resetLink Password reset link
   * @returns Success status
   */
  const sendPasswordReset = async (
    email: string,
    name: string,
    resetLink: string
  ): Promise<boolean> => {
    try {
      // First try to use the Edge Function
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject: 'Reset Your Password - Made in BD',
        emailType: 'password_reset',
        name,
        resetLink
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      setError('Email service not available - production configuration required');
      return false;
    } catch (err) {
      console.error('Error sending password reset email:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return false;
    }
  };

  /**
   * Send notification email
   * @param email Recipient email
   * @param name Recipient name
   * @param subject Email subject
   * @param message Notification message
   * @param actionLink Optional call-to-action link
   * @param actionText Optional call-to-action text
   * @returns Success status
   */
  const sendNotification = async (
    email: string,
    name: string,
    subject: string,
    message: string,
    actionLink?: string,
    actionText?: string
  ): Promise<boolean> => {
    try {
      // First try to use the Edge Function
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject,
        emailType: 'notification',
        name,
        message,
        actionLink,
        actionText
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      setError('Email service not available - production configuration required');
      return false;
    } catch (err) {
      console.error('Error sending notification email:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return false;
    }
  };

  /**
   * Send supplier verification email
   * @param email Supplier email
   * @param name Supplier name or organization
   * @param verificationLink Verification link
   * @returns Success status
   */
  const sendSupplierVerification = async (
    email: string,
    name: string,
    verificationLink: string
  ): Promise<boolean> => {
    try {
      // First try to use the Edge Function
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject: 'Your Supplier Account is Verified - Made in BD',
        emailType: 'notification',
        name,
        verificationLink
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      setError('Email service not available - production configuration required');
      return false;
    } catch (err) {
      console.error('Error sending supplier verification email:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return false;
    }
  };

  /**
   * Send document notification email
   * @param email Recipient email
   * @param name Recipient name
   * @param documentType Document type
   * @param orderNumber Order number
   * @param documentLink Document link
   * @returns Success status
   */
  const sendDocumentNotification = async (
    email: string,
    name: string,
    documentType: string,
    orderNumber: string,
    documentLink: string
  ): Promise<boolean> => {
    try {
      // First try to use the Edge Function
      const edgeFunctionResult = await sendEmailViaEdgeFunction({
        to: email,
        subject: `New Document Available: ${documentType} for Order #${orderNumber}`,
        emailType: 'notification',
        name,
        documentType,
        orderNumber,
        documentLink
      });

      if (edgeFunctionResult) {
        return true;
      }

      // Production email service required
      setError('Email service not available - production configuration required');
      return false;
    } catch (err) {
      console.error('Error sending document notification email:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      return false;
    }
  };

  return {
    sendVerification,
    sendOrderConfirmation,
    sendPasswordReset,
    sendNotification,
    sendSupplierVerification,
    sendDocumentNotification,
    loading,
    error
  };
};